import * as PIXI from 'pixi.js';
import * as PIXIFilters from 'pixi-filters';
import FontFaceObserver from 'fontfaceobserver';
import { CONSTANTS, TEXT_ANCHOR_POINTS, FONT_FALLBACKS } from '../../PreviewDisplayConstants';
import DriftEffect from '../../effects/drift/DriftEffect';
import { getEffectiveDrift, VISUALIZER_ANCHORS } from '../../effects/drift/driftUtilities';
import { isGoogleFontSource, loadGoogleFont } from '../../../../../../utilities/fontUtilities';

const baseDropShadowBlur = 4;
const baseDropShadowDistance = 4;
const baseGlowPadding = 100;
const baseHeight = 500;
// Text is rendered at twice its size and displayed at half, which is what keeps it crisp.
const baseScale = 0.5;
const maxBloomScale = 3;
const shadowScaleFactor = 1.44;

export default class TextElement {
    constructor(container) {
        this.container = container;
        this.elementContainer = new PIXI.Container();
        this.glowContainer = new PIXI.Container();

        this.text = new PIXI.Text('');
        this.glowText = new PIXI.Text('');

        this.text.style = new PIXI.TextStyle({
            fill: 0x000000,
            fontFamily: 'Arial',
            fontWeight: 'normal',
            fontSize: 16,
            align: 'left',
            padding: 0,
            // Shadow settings by default
            dropShadow: false,
            dropShadowAlpha: 1,
            dropShadowBlur: baseDropShadowBlur,
            dropShadowColor: '0x000000',
            dropShadowDistance: baseDropShadowDistance,
            dropShadowAngle: 45 * Math.PI / 180
        });

        this.glowText.style = this.text.style.clone();
        this.glowText.style.dropShadow = false;

        this.bloomFilter = new PIXIFilters.AdvancedBloomFilter({
            threshold: 0.12,
            quality: 10
        });
        this.bloomFilter.enabled = false;

        this.glowSmoothingFilter = new PIXI.filters.BlurFilter();
        this.glowSmoothingFilter.enabled = false;

        this.glowContainer.filters = [this.bloomFilter, this.glowSmoothingFilter];
        this.glowContainer.addChild(this.glowText);

        this.elementContainer.addChild(this.glowContainer);
        this.elementContainer.addChild(this.text);

        this.container.addChild(this.elementContainer);
        this._currentLoadedFont = '';
        this.driftEffect = new DriftEffect();
        this.config = {};
    }

    update(config, index) {
        if (!config) {
            this.setVisibility(false);
            return;
        }

        this.config = config;

        this.setVisibility(true);

        if (this.elementContainer.zIndex !== index)
            this.elementContainer.zIndex = index;

        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;
        const style = this.text.style;
        const glowStyle = this.glowText.style;

        const glowSettings = config.glowSettings || {
            enabled: false,
            blur: 0,
            scale: 1
        };

        const glowEnabled = Boolean(glowSettings.enabled);
        const glowBlur = (glowSettings.blur ?? 0) * multiplier;
        const glowScale = glowSettings.scale ?? 1;
        const glowScaleForBloom = Math.min(maxBloomScale, 1 + Math.max(0, glowScale - 1) * 0.25);
        const glowSmoothingBlur = glowBlur * 0.3;

        this.glowContainer.visible = glowEnabled;
        this.bloomFilter.enabled = glowEnabled;
        this.bloomFilter.blur = glowBlur;
        this.bloomFilter.bloomScale = glowScaleForBloom;
        this.bloomFilter.padding = Math.max(baseGlowPadding * multiplier, glowBlur * 8);

        this.glowSmoothingFilter.enabled = glowEnabled;
        this.glowSmoothingFilter.blur = glowSmoothingBlur;
        this.glowSmoothingFilter.quality = 4;

        // Shadow configuration
        const shadowSettings = config.shadowSettings || {
            enabled: config.shadowSettings?.enabled ?? false,
            color: '0x000000',
            blur: baseDropShadowBlur,
            opacity: 1,
            distance: baseDropShadowDistance,
            angle: 30
        };

        const shadowEnabled = Boolean(shadowSettings.enabled);

        if (style.dropShadow !== shadowEnabled)
            style.dropShadow = shadowEnabled;

        const blur = shadowSettings.blur;
        const distance = shadowSettings.distance;
        const scaledBlur = (blur * multiplier) / shadowScaleFactor;
        const scaledDistance = (distance * multiplier) / shadowScaleFactor;

        // Update shadow properties (even if disabled, to keep style in sync)
        style.dropShadowBlur = scaledBlur;
        style.dropShadowAlpha = shadowSettings.opacity;
        style.dropShadowColor = shadowSettings.color;
        style.dropShadowDistance = scaledDistance;
        style.dropShadowAngle = (shadowSettings.angle ?? 45) * Math.PI / 180;

        // Keep glow text geometry aligned with the base text when shadow is enabled.
        // AdvancedBloomFilter renders source + bloom, so mismatched metrics can show
        // an extra shifted text copy when shadow distance/angle are large.
        glowStyle.dropShadow = shadowEnabled;
        glowStyle.dropShadowBlur = scaledBlur;
        glowStyle.dropShadowColor = shadowSettings.color;
        glowStyle.dropShadowDistance = scaledDistance;
        glowStyle.dropShadowAngle = style.dropShadowAngle;
        glowStyle.dropShadowAlpha = 0;

        // Occasionally some fonts are cropped. Adding some padding will prevent it.
        // We also add effect-specific padding to prevent clipping when shadow/glow is visible.
        const basePadding = (config.fontSize * multiplier) / 3;
        const shadowPadding = shadowEnabled ? (scaledDistance + scaledBlur) : 0;
        const glowPadding = glowEnabled ? (glowBlur * Math.max(1, glowScale)) : 0;
        const fontPadding = basePadding + shadowPadding + glowPadding;

        if (style.padding !== fontPadding)
            style.padding = fontPadding;

        if (glowStyle.padding !== fontPadding)
            glowStyle.padding = fontPadding;

        if (style.fill !== config.color)
            style.fill = config.color;

        if (glowStyle.fill !== config.color)
            glowStyle.fill = config.color;

        const newFontWeight = config.bold ? '700' : '400';
        const fontFamilyChain = [
            config.font,
            ...FONT_FALLBACKS.PRIMARY_FALLBACKS,
            ...FONT_FALLBACKS.CJK_FONTS,
            ...FONT_FALLBACKS.SINHALA_FONTS,
        ].join(',');

        if (style.fontFamily !== fontFamilyChain || style.fontWeight !== newFontWeight) {
            style.fontFamily = fontFamilyChain;
            style.fontWeight = newFontWeight;
            glowStyle.fontFamily = fontFamilyChain;
            glowStyle.fontWeight = newFontWeight;
            this.loadFont(config.font, newFontWeight, config.fontSource);
        }

        const newFontSize = config.fontSize * multiplier;
        if (style.fontSize !== newFontSize)
            style.fontSize = newFontSize;

        if (glowStyle.fontSize !== newFontSize)
            glowStyle.fontSize = newFontSize;

        const newAlign = this.getTextAlign(config.anchorPoint);
        if (style.align !== newAlign)
            style.align = newAlign;

        if (glowStyle.align !== newAlign)
            glowStyle.align = newAlign;

        const wordWrapWidth = CONSTANTS.TEXT_BASE_WRAP_WIDTH * multiplier;
        
        if (!style.wordWrap || style.wordWrapWidth !== wordWrapWidth) {
            style.wordWrap = true;
            style.wordWrapWidth = wordWrapWidth;
        }

        if (!glowStyle.wordWrap || glowStyle.wordWrapWidth !== wordWrapWidth) {
            glowStyle.wordWrap = true;
            glowStyle.wordWrapWidth = wordWrapWidth;
        }

        if (this.text.text !== config.text)
            this.text.text = config.text;

        if (this.glowText.text !== config.text)
            this.glowText.text = config.text;

        this.applyTransform(config);
        this.updateAnchors(config.anchorPoint);
    }

    updateAnchors(anchorPoint) {
        let newAnchorX, newAnchorY;
        switch (anchorPoint) {
            case TEXT_ANCHOR_POINTS.CENTER:
                newAnchorX = 0.5;
                newAnchorY = 0.5;
                break;
            case TEXT_ANCHOR_POINTS.LEFT:
                newAnchorX = 0;
                newAnchorY = 0.5;
                break;
            case TEXT_ANCHOR_POINTS.RIGHT:
                newAnchorX = 1;
                newAnchorY = 0.5;
                break;
            default:
                newAnchorX = 0;
                newAnchorY = 0.5;
        }

        if (this.text.anchor.x !== newAnchorX || this.text.anchor.y !== newAnchorY) {
            this.text.anchor.set(newAnchorX, newAnchorY);
            this.glowText.anchor.set(newAnchorX, newAnchorY);
        }
    }

    loadFont(fontFamily, fontWeight, fontSource) {
        const fontKey = `${fontFamily}-${fontWeight}-${fontSource}`;

        if (this._currentLoadedFont === fontKey)
            return;

        this._currentLoadedFont = fontKey;

        if (isGoogleFontSource(fontSource))
            loadGoogleFont(fontFamily);

        const font = new FontFaceObserver(fontFamily, { weight: fontWeight });

        font.load(null, 20000).then(() => {
            this.refreshPixiText(this.text);
            this.refreshPixiText(this.glowText);
        }).catch(e => {
            console.warn(`Failed to load font ${fontFamily} with weight ${fontWeight}:`, e);
        });
    }

    refreshPixiText(textElement) {
        if (textElement._autoResolution !== undefined) {
            textElement.dirty = true;
            return;
        }

        textElement.updateText(true);
    }

    getTextAlign(anchorPoint) {
        switch (anchorPoint) {
            case TEXT_ANCHOR_POINTS.CENTER:
                return 'center';
            case TEXT_ANCHOR_POINTS.LEFT:
                return 'left';
            case TEXT_ANCHOR_POINTS.RIGHT:
                return 'right';
            default:
                return 'left';
        }
    }

    setVisibility(visible) {
        if (this.elementContainer.visible !== visible)
            this.elementContainer.visible = visible;
    }

    changeTextValue(value) {
        if (this.text.text !== value) {
            this.text.text = value;
            this.glowText.text = value;
        }
    }

    applyDrift(audioLevel = 0) {
        if (this.driftEffect.enabled)
            this.driftEffect.applyTo(this.elementContainer, audioLevel);
    }

    applyTransform(config) {
        const effectiveDrift = getEffectiveDrift(config.drift, VISUALIZER_ANCHORS);
        this.driftEffect.updateConfig(effectiveDrift);

        // Always apply the base transform to the PIXI element container immediately.
        // This ensures the text is at the correct position/scale even before
        // applyDrift() runs during draw(). Without this, when drift is enabled,
        // the text would stay at default (0,0)/(1,1) until the first draw() call,
        // causing visible position/size jumps on initial render (e.g. when navigating
        // to the page via Export Video with non-landscape aspect ratios).
        this.elementContainer.scale.set(baseScale, baseScale);
        this.elementContainer.rotation = 0;
        this.moveTo(config.position);

        // When drift is enabled, also set dimensions for edge cover calculation.
        // DriftEffect will override the position/scale above when applyDrift() runs.
        if (this.driftEffect.enabled) {
            this.driftEffect.setDimensions(this.text.width * baseScale, this.text.height * baseScale);
        }
    }

    // Separate from applyTransform so a canvas drag can move the line on its own, without a round trip
    // through the store, and still land on the same pixel the stored position means. The drift effect
    // is told as well: it holds the resting position it plays around, and would otherwise pull the
    // line back to where it was on the very next frame.
    moveTo(position) {
        const multiplier = CONSTANTS.DEFAULT_HEIGHT / baseHeight;
        const basePosition = { x: position.x * multiplier, y: position.y * multiplier };

        this.elementContainer.position.set(basePosition.x, basePosition.y);
        this.driftEffect.setBaseTransform(basePosition, 0, { x: baseScale, y: baseScale });
    }

    destroy() {
        this.driftEffect.destroy();
        this.elementContainer.destroy({ children: true });
    }
}
